<template>
  <v-card outlined>
    <v-card-title class="align-start">
      <v-row>
        <v-col cols="12"> Résultats </v-col>
      </v-row>
      <dot-menu
        :isLoading="page.loading"
        @exportExcel="exportExcel"
        :excelButton="{
          excelData: excelButton.data,
          filename: `vasts-costs-${startDate}_${endDate}.xlsx`,
          disabled: exportDisabled,
        }"
      />
    </v-card-title>
    <v-card-text>
      <v-data-table
        :headers="page.headers"
        :items="page.items"
        :loading="page.loading"
        :hide-default-footer="true"
        :disable-pagination="true"
        :disable-filtering="true"
        :sort-by="'site'"
      />
      <!-- :disable-sort="true" -->
    </v-card-text>
    <progress-bar :download="download" />
  </v-card>
</template>

<script>
import { computed, reactive, watch } from "@vue/composition-api";

import DotMenu from "@/components/common/menus/DotMenu";
import ProgressBar, {
  download_initial_state,
  resetDownload,
} from "@/components/common/ProgressBar";

import useAxios from "@/hooks/useAxios";
import useProxy from "@/hooks/useProxy";

import { getStrDateFromDateStr } from "@/utils/dates";
import { formatCurrency } from "@/utils/formatting";

export default {
  name: "Results",
  components: {
    DotMenu,
    ProgressBar,
  },
  setup(props, { emit }) {
    const { store } = useProxy();
    const { axiosGet } = useAxios();

    const page = reactive({
      loading: false,
      headers: [
        {
          text: "Site",
          align: "start",
          value: "site",
        },
        {
          text: "Ad_VASTs_cost",
          value: "ad_vasts_cost",
        },
        {
          text: "Content_cost",
          value: "content_cost",
        },
        {
          text: "Adserving_VASTs_revenues",
          value: "adserving_vast_revenue",
        },
        {
          text: "Booster_VASTs_revenues",
          value: "booster_vast_revenue",
        },
        {
          text: "Date",
          value: "date",
        },
      ],
      items: [],
    });

    const download = reactive(download_initial_state());

    const excelButton = reactive({
      data: new Blob([""], { type: "text/plain" }),
    });

    // =>  METHODS
    const checkDates = () => {
      if (
        !startDate.value ||
        startDate.value.length !== 10 ||
        !endDate.value ||
        endDate.value.length !== 10
      ) {
        alert("Sélectionnez une plage de date !");
        return false;
      }

      return true;
    };

    const formatResult = (data) => {
      const result = [];
      for (const [site_name, rows] of Object.entries(data)) {
        for (const row of rows) {
          result.push({
            site: site_name,
            ad_vasts_cost: formatCurrency(row["ad_vast_cost"]),
            content_cost: formatCurrency(row["content_cost"]),
            adserving_vast_revenue: formatCurrency(
              row["adserving_vast_revenue"]
            ),
            booster_vast_revenue: formatCurrency(row["booster_vast_revenue"]),
            date: getStrDateFromDateStr(row["date"]),
          });
        }
      }

      return result;
    };

    const getData = async () => {
      if (applyFilters.value && !page.loading && checkDates()) {
        page.loading = true;
        const { data } = await axiosGet("/regie/vasts-costs", {
          start_date: startDate.value,
          end_date: endDate.value,
        });

        page.items = formatResult(data);

        page.loading = false;

        // Force clicking on button again to re-run that request :
        // (we don't call this dispatch earlier to be sure, as state is async, that all listening components could run their requests)
        store.dispatch("common/updateApplyFilters", false);
      }
    };

    const exportExcel = async () => {
      if (!checkDates()) {
        return;
      }

      page.loading = true;
      download.running = true;

      try {
        const { data } = await axiosGet(
          "/regie/vasts-costs/export_xlsx",
          {
            start_date: startDate.value,
            end_date: endDate.value,
          },
          {
            responseType: "blob",
            onDownloadProgress: (progressEvent) => {
              download.percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
            },
          }
        );

        excelButton.data = data;
      } catch (error) {
        console.error("error while fetching data");
        resetDownload(download);
      }

      page.loading = false;
    };

    // =>  COMPUTED
    const applyFilters = computed(
      () => store.getters["common/getApplyFilters"]
    );
    const startDate = computed(() => store.getters["regie/getDates"][0]);
    const endDate = computed(() => store.getters["regie/getDates"][1]);

    const exportDisabled = computed(
      () => page.loading || page.items.length === 0
    );

    // =>  WATCH
    watch(applyFilters, () => {
      getData();
    });

    watch(
      () => download.percentCompleted,
      (newValue) => {
        if (newValue >= 100) {
          setTimeout(() => {
            resetDownload(download);
          }, 500);
        }
      }
    );

    // =>  HOOKS

    // =>  DATA
    return {
      page,
      download,
      exportExcel,
      excelButton,
      exportDisabled,
      startDate,
      endDate,
    };
  },
};
</script>

<style></style>
